import { useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useGetCompetitiveIntelligenceKpisQuery } from 'src/context/api/competitorsMonitoring/api';
import { fDate } from 'src/utils/format-time';
import countriesJson from 'src/utils/countries.json';
import { CountryItem } from '../types';

// Import du contexte pour vérifier s'il existe
import { CompetitiveIntelligenceContext } from '../components/CompetitiveIntelligenceProvider';

export const useCountryData = () => {
    // Essayer d'utiliser le contexte si disponible
    const context = useContext(CompetitiveIntelligenceContext);
    
    // Si le contexte est disponible, l'utiliser
    if (context) {
        return {
            countriesData: context.countriesData,
            isLoading: context.isLoadingBaseData,
        };
    }
    
    // Sinon, fallback vers l'ancien comportement (standalone)
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    // Fetch competitive intelligence data to get countries
    const { data: competitiveIntelligenceData, isFetching: isLoadingBaseData } = useGetCompetitiveIntelligenceKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });

    // Extract countries from competitive intelligence data
    const ciItems: any[] = useMemo(() => 
        Array.isArray(competitiveIntelligenceData) ? competitiveIntelligenceData : [], 
        [competitiveIntelligenceData]
    );
    
    const countriesList = useMemo(() => {
        const uniqueCountryNames = Array.from(new Set(ciItems.map((it: any) => it?.country_name).filter(Boolean)));
        return uniqueCountryNames.map((name: string) => {
            const found = countriesJson?.find((c: any) => c.name === name);
            return { country_name: name, country_code: found ? found.code : name?.slice(0, 2).toLowerCase() };
        });
    }, [ciItems]);

    // Map countries with their competitive levels
    const countryLevelByName = useMemo(() => {
        const severityRank: Record<string, number> = { low: 0, active: 1, high: 2 };
        const levels = new Map<string, string>();
        for (const item of ciItems) {
            const countryName: string | undefined = (item?.country_name ?? item?.country) as string | undefined;
            const level: string | undefined = (item?.level ?? item?.abs_level ?? item?.severity_level) as string | undefined;
            if (!countryName || !level) continue;
            const current = levels.get(countryName);
            if (!current || (severityRank[level] ?? -1) > (severityRank[current] ?? -1)) {
                levels.set(countryName, level);
            }
        }
        return levels;
    }, [ciItems]);

    // Combine countries list with their levels
    const countriesData: CountryItem[] = useMemo(() => {
        return countriesList.map(country => ({
            ...country,
            level: countryLevelByName.get(country.country_name)
        }));
    }, [countriesList, countryLevelByName]);

    return {
        countriesData,
        isLoading: isLoadingBaseData,
    };
};
